import { Box, Typography, useTheme } from '@mui/material';
import { useDrawRequestQueryApi } from '@hooks/api/useDrawRequestQueryApi';
import { LoanProgressModel } from '@models/drawRequests/loanProgressModel';
import { BudgetEditItem } from '@models/drawRequests/budgetEditItem';
import { useApp } from '@context/AppContext';
import {
  Container,
  AmountAvailableContainer,
  ProgressBarContainer,
  ProgressBarBackground,
  DisbursedProgressBar,
  DrawRequestProgressBar,
  ProgressLabel,
  ProgressPercentage,
  FooterContainer,
} from './LoanProgress.styles';

interface LoanProgressProps {
  readonly sourceRecId: string;
  readonly budgetItems?: BudgetEditItem[];
  readonly isDisbursed?: boolean;
}

export default function LoanProgress({
  sourceRecId,
  budgetItems = [],
  isDisbursed = false,
}: LoanProgressProps) {
  const theme = useTheme();
  const { isTabletScreen } = useApp();
  const isMobile = isTabletScreen;
  const { data: loanProgress } =
    useDrawRequestQueryApi.useFetchLoanProgress(sourceRecId);
  const {
    budgetAmount = 0,
    amountAvailable = 0,
    amountDisbursed = 0,
  } = (loanProgress as LoanProgressModel) || {};

  const totalRequestAmount = budgetItems.reduce(
    (sum, item) => sum + (item.requestAmount || 0),
    0
  );

  const calculatePercentage = (numerator: number, denominator: number) =>
    denominator ? (Number(numerator) / Number(denominator)) * 100 : 0;
  const clampValue = (value: number, min = 0, max = 100) =>
    Math.max(min, Math.min(max, value));

  const disbursedPercentageRaw = calculatePercentage(
    amountDisbursed,
    budgetAmount
  );
  const requestPercentageRaw = calculatePercentage(
    totalRequestAmount,
    budgetAmount
  );

  const disbursedPercentage = clampValue(disbursedPercentageRaw);
  const requestPercentage = clampValue(
    requestPercentageRaw,
    0,
    100 - disbursedPercentage
  );

  const baseLabelPosition = disbursedPercentage + requestPercentage / 2;
  const labelPosition = clampValue(baseLabelPosition, 10, 90);

  return (
    <Container>
      <AmountAvailableContainer>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            marginBottom: '10px',
          }}
        >
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Amount Avail.
          </Typography>
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: 'text.primary',
            }}
          >
            {' '}
            <strong>${amountAvailable.toLocaleString()}</strong>
          </Typography>
        </Typography>
      </AmountAvailableContainer>

      <ProgressBarContainer>
        <ProgressBarBackground />

        {disbursedPercentage > 0 && (
          <DisbursedProgressBar
            sx={{
              width: `${disbursedPercentage}%`,
              borderTopRightRadius: requestPercentage > 0 && !isDisbursed ? 0 : 5,
              borderBottomRightRadius: requestPercentage > 0 && !isDisbursed ? 0 : 5,
            }}
          />
        )}

        {requestPercentage > 0 && !isDisbursed && (
          <DrawRequestProgressBar
            sx={{
              left: `${disbursedPercentage}%`,
              width: `${requestPercentage}%`,
              borderTopLeftRadius: disbursedPercentage > 0 ? 0 : 5,
              borderBottomLeftRadius: disbursedPercentage > 0 ? 0 : 5,
              marginLeft: disbursedPercentage > 0 ? '-0.5px' : 0,
            }}
          />
        )}

        {requestPercentage > 0 && !isDisbursed && (
          <ProgressLabel
            sx={{
              left: `${labelPosition}%`,
              ...(isMobile && {
                maxWidth: 'calc(100% - 20px)',
                overflow: 'visible',
              }),
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: isMobile ? '12px' : '14px',
              }}
            >
              ${totalRequestAmount.toLocaleString()}{' '}
              <ProgressPercentage>
                +{requestPercentageRaw.toFixed(1)}%
              </ProgressPercentage>
            </Typography>
          </ProgressLabel>
        )}
      </ProgressBarContainer>

      {isDisbursed && totalRequestAmount > 0 && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontStyle: 'italic',
              color: 'text.secondary',
              fontSize: '12px',
            }}
          >
            Includ. this disbursement:{' '}
            <Typography
              component="span"
              sx={{
                color: theme.palette.green[500],
                fontWeight: 600,
                fontStyle: 'normal',
              }}
            >
              ${totalRequestAmount.toLocaleString()}
            </Typography>
            {' - '}
            {requestPercentageRaw.toFixed(1)}% of budget
          </Typography>
        </Box>
      )}

      <FooterContainer>
        <Typography variant="body1">
          <Typography
            component="span"
            variant="body1"
            sx={{
              color: 'text.secondary',
            }}
          >
            Amount Disbursed
          </Typography>{' '}
          <Typography
            component="span"
            variant="body1"
            sx={{
              color: 'text.primary',
            }}
          >
            <strong>${amountDisbursed.toLocaleString()}</strong>
          </Typography>
        </Typography>
        <Typography variant="body1">
          <Typography
            component="span"
            variant="body1"
            sx={{
              color: 'text.secondary',
            }}
          >
            Budget Amount
          </Typography>{' '}
          <Typography
            component="span"
            variant="body1"
            sx={{
              color: 'text.primary',
            }}
          >
            <strong>${budgetAmount.toLocaleString()}</strong>
          </Typography>
        </Typography>
      </FooterContainer>
    </Container>
  );
}
